import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type BoletoImportItem = {
  id: number;
  status: string;
  createdAt: string;
  dueDate: string | null;
  amount: number | null;
  lineDigitavel: string | null;
  barcode: string | null;
  beneficiaryName: string | null;
  payerName: string | null;
  guia: string | null;
  nossoNumero: string | null;
  idBoleto: string | null;
  extractionJson: Record<string, unknown> | null;
};

type ListResponse = {
  items: BoletoImportItem[];
  page: number;
  pageSize: number;
  total: number;
};

export const BoletoImportListPage: React.FC = () => {
  const apiUrl = import.meta.env.VITE_API_URL as string;
  const token = useMemo(
    () => localStorage.getItem("mpf_admin_access_token"),
    []
  );
  const [filters, setFilters] = useState({
    status: "",
    documentType: "",
    dueYear: "",
    q: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (currentPage: number, currentFilters: typeof appliedFilters) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("pageSize", "20");
        if (currentFilters.status) {
          params.set("status", currentFilters.status);
        }
        if (currentFilters.documentType) {
          params.set("documentType", currentFilters.documentType);
        }
        if (currentFilters.dueYear) {
          params.set("dueYear", currentFilters.dueYear);
        }
        if (currentFilters.q) {
          params.set("q", currentFilters.q);
        }
        const response = await fetch(
          `${apiUrl}/api/boleto-imports?${params.toString()}`,
          {
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const payload = (await response.json()) as ListResponse & {
          error?: string;
        };
        if (!response.ok) {
          throw new Error(payload.error || "Falha ao carregar boletos.");
        }
        setData(payload);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Erro ao carregar boletos."
        );
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, token]
  );

  useEffect(() => {
    void load(page, appliedFilters);
  }, [load, page, appliedFilters]);

  const handleApply = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    const confirmed = window.confirm("Excluir este boleto importado?");
    if (!confirmed) {
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/boleto-imports/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok && response.status !== 204) {
        const payload = (await response.json()) as { error?: string };
        throw new Error(payload.error || "Falha ao excluir boleto.");
      }
      void load(page, appliedFilters);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erro ao excluir boleto."
      );
    }
  };

  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const resolveDocumentType = (item: BoletoImportItem) => {
    const extraction = item.extractionJson as { documentType?: string } | null;
    return extraction?.documentType ?? "UNKNOWN";
  };

  const resolveIdentifier = (item: BoletoImportItem) => {
    return (
      item.lineDigitavel ??
      item.guia ??
      item.nossoNumero ??
      item.idBoleto ??
      item.barcode ??
      "-"
    );
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="mb-0">Boletos importados</h1>
        <Link className="btn btn-outline-primary" to="/financeiro/boletos/importar">
          Importar boleto
        </Link>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, status: event.target.value }))
                }
              >
                <option value="">Todos</option>
                <option value="DRAFT">DRAFT</option>
                <option value="ERROR">ERROR</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Tipo</label>
              <select
                className="form-select"
                value={filters.documentType}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    documentType: event.target.value,
                  }))
                }
              >
                <option value="">Todos</option>
                <option value="BOLETO_BANCARIO">BOLETO_BANCARIO</option>
                <option value="ARRECADACAO_PUBLICA">ARRECADACAO_PUBLICA</option>
                <option value="UNKNOWN">UNKNOWN</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Ano vencimento</label>
              <input
                type="number"
                className="form-control"
                value={filters.dueYear}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, dueYear: event.target.value }))
                }
                placeholder="2026"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Busca</label>
              <input
                type="text"
                className="form-control"
                value={filters.q}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, q: event.target.value }))
                }
                placeholder="Buscar por linha, guia, processo, nome"
              />
            </div>
            <div className="col-md-1 d-grid">
              <button className="btn btn-primary" onClick={handleApply}>
                Atualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Data importacao</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Beneficiario</th>
                  <th>Identificador</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.length ? (
                  data.items.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDateBR(item.createdAt)}</td>
                      <td>{resolveDocumentType(item)}</td>
                      <td>{item.status}</td>
                      <td>
                        {item.amount != null ? formatMoneyBR(item.amount) : "-"}
                      </td>
                      <td>
                        {item.dueDate ? formatDateBR(item.dueDate) : "-"}
                      </td>
                      <td>{item.beneficiaryName ?? item.payerName ?? "-"}</td>
                      <td className="text-break">{resolveIdentifier(item)}</td>
                      <td className="text-end">
                        <div className="btn-group btn-group-sm">
                          <Link
                            className="btn btn-outline-secondary"
                            to={`/financeiro/boletos/${item.id}`}
                          >
                            Ver
                          </Link>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(item.id)}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      Nenhum boleto encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="text-muted">Total: {total}</div>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Anterior
              </button>
              <button className="btn btn-outline-secondary" disabled>
                {page} / {totalPages}
              </button>
              <button
                className="btn btn-outline-secondary"
                disabled={page >= totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Proximo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
